"use client";

import type {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
} from "react";
import { useCallback } from "react";
import {
  MIN_ZOOM_DELTA,
  selectAnchoredScrollLeft,
  type ApplyZoomAtAnchorParams,
} from "./gantt-zoom-control-math";
import { clampPxPerDay } from "./layout-gantt";

interface UseGanttAnchoredZoomParams {
  fitPxPerDayRef: MutableRefObject<number>;
  setPxPerDayOverride: Dispatch<SetStateAction<number | null>>;
  timelineScrollRef: RefObject<HTMLDivElement | null>;
}

export function useGanttAnchoredZoom({
  fitPxPerDayRef,
  setPxPerDayOverride,
  timelineScrollRef,
}: UseGanttAnchoredZoomParams): (params: ApplyZoomAtAnchorParams) => void {
  return useCallback(
    ({ anchorViewportX, scaleFactor }: ApplyZoomAtAnchorParams): void => {
      const timelineNode = timelineScrollRef.current;
      if (!timelineNode) {
        return;
      }

      const anchorContentX = anchorViewportX + timelineNode.scrollLeft;

      setPxPerDayOverride((previousOverride) => {
        const previousPxPerDay = previousOverride ?? fitPxPerDayRef.current;
        const nextPxPerDay = clampPxPerDay(previousPxPerDay * scaleFactor);

        if (Math.abs(nextPxPerDay - previousPxPerDay) < MIN_ZOOM_DELTA) {
          return previousOverride;
        }

        const ratio = nextPxPerDay / previousPxPerDay;
        const nextScrollLeft = selectAnchoredScrollLeft({
          anchorContentX,
          anchorViewportX,
          ratio,
        });

        window.requestAnimationFrame(() => {
          timelineNode.scrollLeft = nextScrollLeft;
        });

        return nextPxPerDay;
      });
    },
    [fitPxPerDayRef, setPxPerDayOverride, timelineScrollRef],
  );
}
